(() => {
  'use strict';
  if (customElements.get('kik-accessories')) return;
  const core = window.KikAccessoryCore;
  // Shared by component instances, reset only on a new page load.
  const analyticsByProduct = new Map();
  class Accessories extends HTMLElement {
    async connectedCallback() {
      await customElements.whenDefined('xo-product');
      if (!this.isConnected || this.ready || !core || !window.xoStore || !window.cartFormSubscribe) return;
      this.product = this.closest('xo-product');
      if (!this.product) return;
      this.ready = true;
      if (!analyticsByProduct.has(this.dataset.productId)) analyticsByProduct.set(this.dataset.productId, new Set());
      this.analyticsSent = analyticsByProduct.get(this.dataset.productId);
      this.messages = JSON.parse(this.querySelector('[data-messages]').textContent);
      this.purchase = this.purchase || new core.Purchase((...args) => window.fetch(...args), window.Shopify.routes.root);
      this.shopPayCheckout ||= new core.ShopPayCheckout((...args) => window.fetch(...args));
      this.entries = [...this.querySelectorAll('[data-entry]')].map((element) => {
        const select = element.querySelector('[data-variant]');
        const available = [...select.options].filter((option) => option.value && !option.disabled);
        if (available.length) {
          if (!available.some((option) => option.value === select.value)) select.value = available[0].value;
          [...select.options].filter((option) => !option.value).forEach((option) => option.remove());
        }
        return { element, select, checkbox: element.querySelector('[data-select]'), available,
          scope: element.dataset.scope, keyboardVariants: element.dataset.keyboardVariants.split(',').filter(Boolean) };
      });
      this.entries.forEach((entry, index) => this.buildVariantPicker(entry, index));
      this.outsidePicker = (event) => {
        this.entries.forEach((entry) => { if (!entry.picker.contains(event.target)) this.closePicker(entry); });
      };
      document.addEventListener('click', this.outsidePicker);
      this.change = (event) => {
        if (this.contains(event.target)) {
          this.update();
        }
      };
      this.capture = (event) => this.handlePurchase(event);
      this.cardClick = (event) => {
        if (event.target.closest('select, option, input, label, button, a, .kik-variant-picker')) return;
        const card = event.target.closest('[data-entry]');
        const entry = this.entries.find((item) => item.element === card);
        if (entry && !entry.checkbox.disabled) entry.checkbox.click();
      };
      this.addEventListener('change', this.change);
      this.addEventListener('click', this.cardClick);
      this.product.addEventListener('click', this.capture, true);
      this.product.addEventListener('submit', this.capture, true);
      this.unsubscribe = window.cartFormSubscribe(() => this.update());
      this.productUnsubscribe = window.productSubscribe(() => this.update());
      this.cartUnsubscribe = window.cartSubscribe?.((cart) => {
        if (cart.addIdLoading === `${this.dataset.sectionId}/${this.dataset.productId}` && cart.isAdded === false) this.nativeRequest = this.state();
        if (!this.nativeRequest || cart.addIdLoading) return;
        const request = this.nativeRequest;
        this.nativeRequest = null;
        if (cart.isAdded && !cart.addErrorMessage && String(cart.item?.product_id) === this.dataset.productId
          && String(cart.item?.variant_id || cart.item?.id) === String(request.variant?.id)
          && Number(cart.item?.quantity) >= request.quantity) this.trackSuccess();
      });
      this.priceObserver = new MutationObserver(() => { this.updateShopPayButtons(); this.updateStickyPrice(); if (this.purchase.busy || this.checkoutPending) this.setPurchaseLoading(true); });
      this.priceObserver.observe(this.product, { childList: true, subtree: true });
      this.restorePage = (event) => {
        if (!event.persisted) return;
        this.checkoutPending = false;
        this.setPurchaseLoading(false);
        this.removeAttribute('aria-busy');
        this.update();
      };
      window.addEventListener('pageshow', this.restorePage);
      this.update();
      this.visibleCards = new Set();
      this.exposureSince = new Map();
      this.exposure = new IntersectionObserver((entries) => {
        entries.forEach((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.5
          ? this.visibleCards.add(entry.target) : this.visibleCards.delete(entry.target));
        this.checkExposure();
      }, { threshold: [0, 0.5] });
      this.entries.forEach((entry) => this.exposure.observe(entry.element));
      this.exposureChanged = () => this.checkExposure();
      document.addEventListener('visitorConsentCollected', this.exposureChanged);
      document.addEventListener('visibilitychange', this.exposureChanged);
      this.exposurePoll = setInterval(this.exposureChanged, 250);
    }
    disconnectedCallback() {
      window.removeEventListener('pageshow', this.restorePage);
      this.unsubscribe?.(); this.productUnsubscribe?.(); this.exposure?.disconnect();
      this.cartUnsubscribe?.();
      clearInterval(this.exposurePoll);
      document.removeEventListener('visitorConsentCollected', this.exposureChanged);
      document.removeEventListener('visibilitychange', this.exposureChanged);
      this.priceObserver?.disconnect();
      this.setPurchaseLoading(false);
      document.removeEventListener('click', this.outsidePicker);
      this.entries?.forEach((entry) => { entry.picker.remove(); entry.select.hidden = false; });
      this.product?.querySelectorAll('[data-kik-combined-price]').forEach((node) => node.remove());
      this.product?.querySelectorAll('[data-kik-price-active]').forEach((node) => node.removeAttribute('data-kik-price-active'));
      this.removeEventListener('change', this.change);
      this.removeEventListener('click', this.cardClick);
      this.product?.removeEventListener('click', this.capture, true);
      this.product?.removeEventListener('submit', this.capture, true);
      this.product?.removeAttribute('data-kik-accessories-selected');
      this.ready = false;
    }
    closePicker(entry, focus = false) {
      entry.menu.hidden = true;
      entry.trigger.setAttribute('aria-expanded', 'false');
      if (focus) entry.trigger.focus();
    }
    buildVariantPicker(entry, index) {
      const picker = document.createElement('div');
      picker.className = 'kik-variant-picker';
      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'kik-variant-picker__trigger';
      trigger.setAttribute('aria-haspopup', 'listbox');
      trigger.setAttribute('aria-expanded', 'false');
      const menu = document.createElement('div');
      menu.className = 'kik-variant-picker__menu';
      menu.id = `kik-variants-${this.dataset.sectionId}-${this.dataset.productId}-${index}`;
      menu.setAttribute('role', 'listbox');
      menu.setAttribute('aria-label', entry.element.dataset.title);
      trigger.setAttribute('aria-controls', menu.id);
      menu.hidden = true;
      const buttons = [...entry.select.options].map((option) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'kik-variant-picker__option';
        button.setAttribute('role', 'option');
        button.tabIndex = -1;
        button.textContent = option.textContent.trim();
        button.disabled = option.disabled || !option.value;
        button.dataset.value = option.value;
        button.addEventListener('click', () => {
          entry.select.value = option.value;
          entry.select.dispatchEvent(new Event('change', { bubbles: true }));
          this.closePicker(entry, true);
        });
        menu.append(button);
        return button;
      });
      Object.assign(entry, { picker, trigger, menu, optionButtons: buttons });
      const open = () => {
        this.entries.forEach((other) => { if (other !== entry) this.closePicker(other); });
        menu.hidden = false;
        trigger.setAttribute('aria-expanded', 'true');
        (buttons.find((button) => button.dataset.value === entry.select.value && !button.disabled) || buttons.find((button) => !button.disabled))?.focus();
      };
      trigger.addEventListener('click', () => menu.hidden ? open() : this.closePicker(entry));
      picker.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') { event.preventDefault(); this.closePicker(entry, true); }
        if (event.key === 'Tab') this.closePicker(entry);
        if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
          event.preventDefault();
          if (menu.hidden) { open(); return; }
          const enabled = buttons.filter((button) => !button.disabled);
          const current = enabled.indexOf(document.activeElement);
          const next = event.key === 'Home' ? 0 : event.key === 'End' ? enabled.length - 1 : (current + (event.key === 'ArrowDown' ? 1 : -1) + enabled.length) % enabled.length;
          enabled[next]?.focus();
        }
      });
      picker.addEventListener('focusout', (event) => { if (!picker.contains(event.relatedTarget)) this.closePicker(entry); });
      picker.append(trigger, menu);
      entry.select.after(picker);
      entry.select.hidden = true;
    }
    state() {
      const data = window.xoStore.get('xo-product')?.productData?.[this.dataset.productId];
      const form = window.getCartFormState()?.[`${this.dataset.sectionId}/${this.dataset.productId}`];
      const names = (data?.options || []).map((option) => typeof option === 'string' ? option : option.name);
      return {
        variant: core.selectVariant(data?.variants || [], form?.variantSelected, this.dataset.initialVariant, names),
        quantity: Number(form?.quantity ?? 1),
      };
    }
    selected() { return this.entries.filter((entry) => entry.checkbox.checked && !entry.checkbox.disabled); }
    money(price) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: this.dataset.currency }).format(price / 100);
    }
    update() {
      const state = this.state();
      const variantId = state.variant ? String(state.variant.id) : null;
      const keyboardChanged = this.previousKeyboardVariant !== undefined && this.previousKeyboardVariant !== variantId;
      this.previousKeyboardVariant = variantId;
      for (const entry of this.entries) {
        const allowed = !!state.variant && core.compatible(entry, state.variant.id);
        entry.element.hidden = !allowed;
        if (!allowed && entry.checkbox.checked) entry.checkbox.checked = false;
        if (keyboardChanged) {
          entry.checkbox.checked = false;
          this.closePicker(entry);
        }
        entry.checkbox.disabled = !allowed || !entry.available.length;
        entry.select.disabled = !allowed || !entry.available.length;
        entry.element.querySelector('[data-availability]').textContent = !allowed ? this.messages.incompatible : !entry.available.length ? this.messages.soldOut : '';
        const option = entry.select.selectedOptions[0];
        const image = entry.element.querySelector('.kik-accessories__image');
        if (image && option?.dataset.image) {
          if (image.getAttribute('src') !== option.dataset.image) {
            image.removeAttribute('srcset');
            image.removeAttribute('sizes');
            image.src = option.dataset.image;
          }
          image.alt = option.dataset.imageAlt || entry.element.dataset.title;
        }
        const title = option?.textContent.trim() || this.messages.choose;
        if (entry.trigger.textContent !== title) entry.trigger.textContent = title;
        entry.trigger.setAttribute('aria-label', `${entry.element.dataset.title}: ${title}`);
        entry.trigger.disabled = entry.select.disabled;
        if (entry.select.disabled) this.closePicker(entry);
        entry.optionButtons.forEach((button) => button.setAttribute('aria-selected', String(button.dataset.value === entry.select.value)));
        entry.element.querySelector('[data-price-label]').textContent = option?.value ? this.money(Number(option.dataset.price)) : '';
      }
      const selected = this.selected();
      this.product.toggleAttribute('data-kik-accessories-selected', !!selected.length || this.purchase.busy || this.purchase.uncertain);
      this.updateShopPayButtons();
      this.updateStickyPrice();
      this.updateVisibility();
      if (this.visibleCards) this.checkExposure();
    }
    updateShopPayButtons() {
      const state = this.state();
      this.product.querySelectorAll('[data-shop-pay]').forEach((button) => {
        button.disabled = !state.variant?.available || !Number.isInteger(state.quantity) || state.quantity < 1 || this.purchase.busy || this.purchase.uncertain || !!this.checkoutPending;
      });
    }
    updateVisibility() {
      const hasCompatible = this.entries.some((entry) => !entry.element.hidden);
      this.querySelectorAll('h3, .kik-accessories__note').forEach((node) => { node.hidden = !hasCompatible; });
      this.hidden = !hasCompatible && !this.querySelector('[data-status]').textContent && this.querySelector('[data-review]').hidden;
    }
    updateStickyPrice() {
      const selected = this.selected();
      const { variant, quantity } = this.state();
      const active = selected.length > 0 && !!variant;
      const total = ((variant?.price || 0) + selected.reduce((sum, entry) => sum + Number(entry.select.selectedOptions[0]?.dataset.price || 0), 0)) * quantity;
      for (const container of this.product.querySelectorAll('.xo-sticky-cart__info-price')) {
        container.toggleAttribute('data-kik-price-active', active);
        let price = container.querySelector('[data-kik-combined-price]');
        if (!active) { price?.remove(); continue; }
        if (!price) {
          price = document.createElement('span');
          price.setAttribute('data-kik-combined-price', '');
          price.setAttribute('aria-live', 'polite');
          container.append(price);
        }
        const text = this.money(total);
        if (price.textContent !== text) price.textContent = text;
      }
    }
    setPurchaseLoading(loading) {
      this.loadingButtons ||= new Map();
      if (!loading) {
        for (const [button, original] of this.loadingButtons) {
          button.disabled = original.disabled;
          if (original.ariaBusy === null) button.removeAttribute('aria-busy');
          else button.setAttribute('aria-busy', original.ariaBusy);
          if (original.label) {
            original.label.textContent = original.text;
            if (original.dataText !== null) original.label.setAttribute('data-text', original.dataText);
          }
        }
        this.loadingButtons.clear();
        return;
      }
      for (const button of this.product.querySelectorAll('xo-cart-add button, .xo-product-info-content__buy-now button.but-it, [data-shop-pay]')) {
        if (button.closest('xo-product') !== this.product || this.loadingButtons.has(button)) continue;
        const isCheckout = button.closest('xo-cart-add')?.hasAttribute('xo-checkout-redirection') || button.matches('.but-it, [data-shop-pay]');
        const label = isCheckout ? null : button.querySelector('.xo-btn__text');
        this.loadingButtons.set(button, { disabled: button.disabled, ariaBusy: button.getAttribute('aria-busy'), label,
          text: label?.textContent, dataText: label?.getAttribute('data-text') });
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        if (label) {
          label.textContent = this.messages.busy;
          if (label.hasAttribute('data-text')) label.setAttribute('data-text', this.messages.busy);
        }
      }
    }
    status(message) { this.querySelector('[data-status]').textContent = message; this.updateVisibility(); }
    checkExposure() {
      if (this.analyticsSent.has('accessory_view')) {
        clearInterval(this.exposurePoll); this.exposure.disconnect(); return;
      }
      const allowed = document.visibilityState === 'visible' && window.Shopify.customerPrivacy?.analyticsProcessingAllowed();
      const now = performance.now();
      for (const entry of this.entries) {
        if (!allowed || entry.element.hidden || entry.checkbox.disabled || !this.visibleCards.has(entry.element)) {
          this.exposureSince.delete(entry.element); continue;
        }
        if (!this.exposureSince.has(entry.element)) this.exposureSince.set(entry.element, now);
        if (now - this.exposureSince.get(entry.element) >= 1000) {
          this.track('accessory_view');
          clearInterval(this.exposurePoll); this.exposure.disconnect(); return;
        }
      }
    }
    trackSuccess(accessories = []) {
      if (!this.analyticsSent.has('accessory_view')) return Promise.resolve();
      return Promise.all([this.track('keyboard_add_success'), accessories.length ? this.track('accessory_add_success') : null,
        ...accessories.map((accessory) => this.track('accessory_item_add_success', accessory))]);
    }
    track(name, details = {}) {
      try {
        const detailEvent = name === 'accessory_item_add_success';
        if (!window.Shopify.customerPrivacy?.analyticsProcessingAllowed() || (!detailEvent && this.analyticsSent.has(name))) return;
        if (!detailEvent) this.analyticsSent.add(name);
        return new Promise((resolve) => {
          const timer = setTimeout(resolve, 800);
          (window.dataLayer ||= []).push({ event: name, product_id: this.dataset.productId,
            // Clear item fields on aggregate events so GTM cannot inherit a previous accessory.
            accessory_product_id: null, accessory_name: null, ...details,
            eventCallback: () => { clearTimeout(timer); resolve(); }, eventTimeout: 800 });
        });
      } catch { return false; }
    }
    async handlePurchase(event) {
      if (event.target.closest('xo-product') !== this.product) return;
      const trigger = event.target.closest('xo-cart-add');
      const form = event.target.closest('form[action*="/cart/add"]');
      const shopPay = event.target.closest('[data-shop-pay]');
      const quick = shopPay || event.target.closest('.xo-product-info-content__buy-now button.but-it');
      if (event.type === 'click' && !trigger && !quick) return;
      if (event.type === 'submit' && !form) return;
      const hadSelection = this.selected().length;
      const checkout = !!quick || (trigger?.hasAttribute('xo-checkout-redirection') && trigger.getAttribute('xo-checkout-redirection') !== 'false');
      if (!hadSelection && !checkout && !this.checkoutPending && !this.purchase.busy && !this.purchase.uncertain) return;
      event.preventDefault(); event.stopImmediatePropagation();
      if (this.purchase.busy || this.checkoutPending) return;
      if (this.purchase.uncertain) {
        this.status(this.messages.uncertain); this.querySelector('[data-review]').hidden = false; return;
      }
      this.update();
      const { variant, quantity } = this.state();
      if (!variant?.available || !Number.isInteger(quantity) || quantity < 1) { this.status(this.messages.invalid); return; }
      const selected = this.selected();
      const missing = selected.find((entry) => !entry.select.value || entry.select.selectedOptions[0].disabled);
      if (missing) { this.status(this.messages.choose); missing.select.focus(); return; }
      const properties = {};
      for (const field of this.product.querySelectorAll('[name^="properties["]')) {
        if (field.closest('xo-product') !== this.product || field.disabled || (['checkbox', 'radio'].includes(field.type) && !field.checked)) continue;
        if (!field.checkValidity()) { field.reportValidity(); return; }
        const key = field.name.match(/^properties\[(.*)\]$/)?.[1];
        if (field.type === 'file' && field.files?.length) { this.status(this.messages.invalid); return; }
        if (key && field.value) properties[key] = field.value;
      }
      const plan = this.product.querySelector('[name="selling_plan"]')?.value;
      const items = [{ id: variant.id, quantity, properties, ...(plan ? { selling_plan: plan } : {}) },
        ...selected.map((entry) => ({ id: Number(entry.select.value), quantity }))];
      if (shopPay) {
        this.checkoutPending = true;
        this.status('');
        this.querySelector('[data-review]').hidden = true;
        this.setPurchaseLoading(true);
        this.setAttribute('aria-busy', 'true');
        const result = await this.shopPayCheckout.create(items, { country: this.dataset.country, locale: this.dataset.locale });
        if (result.complete) {
          window.location.assign(result.checkoutUrl);
          return;
        }
        this.checkoutPending = false;
        this.setPurchaseLoading(false);
        this.removeAttribute('aria-busy');
        this.update();
        this.status(this.messages.shopPayFailed);
        this.querySelector('[data-status]').focus();
        return;
      }
      const titles = [variant.public_title || variant.name || variant.title,
        ...selected.map((entry) => entry.element.dataset.title + ' / ' + entry.select.selectedOptions[0].textContent.trim())];
      // Snapshot the submitted selection; the customer can edit cards while the request is pending.
      const accessoryDetails = selected.map((entry) => ({
        accessory_product_id: entry.element.dataset.productId,
        accessory_name: entry.element.dataset.title,
      }));
      const drawer = document.querySelector('xo-cart-mini');
      const sections = drawer?.id ? [drawer.id] : [];
      this.status('');
      const pending = this.purchase.add(items, sections, window.location.pathname);
      this.setPurchaseLoading(true);
      this.setAttribute('aria-busy', 'true'); this.update();
      let result;
      try { result = await pending; }
      finally { this.setPurchaseLoading(false); }
      this.removeAttribute('aria-busy'); this.update();
      if (result.blocked) return;
      if (!result.complete) {
        const details = result.lines?.map((line, index) => `${titles[index]}: ${this.messages.added} ${line.added}, ${this.messages.missing} ${line.missing}`).join('; ');
        this.status(`${result.uncertain ? this.messages.uncertain : this.messages.failed}${details ? ' ' + details : ''}`);
        this.querySelector('[data-review]').hidden = false;
        this.querySelector('[data-status]').focus();
        if (result.cart) window.xoStore.set('xo-cart', (state) => ({ ...state, size: result.cart.item_count }))('accessories/reconcile');
        return;
      }
      this.status('');
      const tracked = this.trackSuccess(accessoryDetails);
      if (checkout) {
        this.checkoutPending = true;
        this.setPurchaseLoading(true);
        await tracked;
        window.location.assign(window.Shopify.routes.root + 'checkout');
        return;
      }
      try {
        const html = result.sections?.[drawer?.id];
        if (!html || !drawer || drawer.hasAttribute('xo-notification')) throw new Error('Cart drawer unavailable');
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const markers = [...drawer.querySelectorAll('xo-cart-will-change')];
        if (!markers.length || markers.some((node) => ![...parsed.querySelectorAll('xo-cart-will-change')].some((next) => next.getAttribute('xo-unique-id') === node.getAttribute('xo-unique-id')))) throw new Error('Incomplete cart section');
        window.xoStore.set('xo-cart', (state) => ({ ...state, size: result.cart.item_count, sections: result.sections,
          addIdLoading: '', addErrorMessage: '', changeLineLoading: -1,
          productIdsForCartNotification: [this.dataset.productId, ...selected.map((entry) => entry.element.dataset.productId)] }))('accessories/add');
        const modal = drawer.closest('xo-modal');
        if (!modal || !window.xoModal) throw new Error('Cart modal unavailable');
        window.xoModal.open(modal.getAttribute('xo-name'));
      } catch { window.location.assign(window.Shopify.routes.root + 'cart'); }
    }
  }
  customElements.define('kik-accessories', Accessories);
})();
